/**
 * File: useLoadADRData.ts
 * Description: Custom hook to handle loading ADR definitions and project data
 * Author: GitHub Copilot
 * Created: 2025-01-XX
 * Last Modified: 2025-01-XX
 */

import { useEffect } from 'react';

import { adrService } from '../../services/adrService';
import { projectService } from '../../services/projectService';
import { ADRDefinition } from '../../types/adr';

interface ProjectData {
  id: number;
  title: string;
  description: string;
}

interface UseLoadADRDataProps {
  projectId: string | undefined;
  onProjectLoaded: (_project: ProjectData | null) => void;
  onDefinitionsLoaded: (_definitions: ADRDefinition[]) => void;
  onLoadingStateChange: (_isLoading: boolean) => void;
}

/**
 * Hook to load ADR definitions and project data when component mounts
 */
export const useLoadADRData = ({
  projectId,
  onProjectLoaded,
  onDefinitionsLoaded,
  onLoadingStateChange,
}: UseLoadADRDataProps): void => {
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        return;
      }

      try {
        onLoadingStateChange(true);
        // Load project data
        const projectData = await projectService.getProject(Number.parseInt(projectId, 10));
        onProjectLoaded(projectData);

        const definitionsData = await adrService.getADRDefinitions(Number.parseInt(projectId, 10));
        onDefinitionsLoaded(definitionsData);
      } catch {
        // Silently handle error - component will show default state
      } finally {
        onLoadingStateChange(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  // Note: Callbacks intentionally excluded from deps as they are stable setState functions
};
